import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useUser } from '../../context/UserContext';
import Icon from '../../components/Icon';
import { resolveLorRosterId } from '../../utils/lorIdentity';

// ── Position colors ────────────────────────────────────────────────────────────

const POS_COLORS = {
  QB:  { color: '#FF6B6B', bg: 'rgba(255,107,107,0.15)' },
  RB:  { color: '#4ECDC4', bg: 'rgba(78,205,196,0.15)' },
  WR:  { color: '#45B7D1', bg: 'rgba(69,183,209,0.15)' },
  TE:  { color: '#F7DC6F', bg: 'rgba(247,220,111,0.15)' },
  K:   { color: '#BDC3C7', bg: 'rgba(189,195,199,0.15)' },
  DEF: { color: '#A29BFE', bg: 'rgba(162,155,254,0.15)' },
};

const GRADE_STYLES = {
  value: { color: '#2ECC71', label: '▲ Value' },
  fair:  { color: '#95A5A6', label: '— Fair'  },
  reach: { color: '#E74C3C', label: '▼ Reach' },
};

const RISK_BADGE = {
  low:    { color: '#2ECC71', label: 'Low Risk'    },
  medium: { color: '#F39C12', label: 'Med Risk'    },
  high:   { color: '#E74C3C', label: 'High Risk'   },
};

function posStyle(pos) {
  return POS_COLORS[pos] || { color: '#BDC3C7', bg: 'rgba(189,195,199,0.15)' };
}

function gradeColor(g) {
  return g === 'A' ? 'var(--green)' : g === 'B' ? '#3498DB' : g === 'C' ? '#F39C12' : g === 'D' ? '#E67E22' : 'var(--red)';
}

// ── CSS keyframes injected once ────────────────────────────────────────────────

const KEYFRAME_STYLE = `
@keyframes pulse-cell {
  0%   { box-shadow: 0 0 0 0 rgba(var(--lor-accent-rgb),0.7); }
  50%  { box-shadow: 0 0 0 6px rgba(var(--lor-accent-rgb),0); }
  100% { box-shadow: 0 0 0 0 rgba(var(--lor-accent-rgb),0); }
}
@keyframes land-in {
  0%   { opacity: 0; transform: scale(0.85) translateY(-4px); }
  100% { opacity: 1; transform: scale(1) translateY(0); }
}
@keyframes slide-up {
  0%   { transform: translateY(100%); opacity: 0; }
  100% { transform: translateY(0);    opacity: 1; }
}
@keyframes fade-in {
  0%   { opacity: 0; }
  100% { opacity: 1; }
}
@keyframes toast-in {
  0%   { transform: translateY(-12px); opacity: 0; }
  100% { transform: translateY(0);     opacity: 1; }
}
`;

let _keyframesInjected = false;
function injectKeyframes() {
  if (_keyframesInjected) return;
  const el = document.createElement('style');
  el.textContent = KEYFRAME_STYLE;
  document.head.appendChild(el);
  _keyframesInjected = true;
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function LORMockDraft({ api }) {
  injectKeyframes();
  const { currentUser } = useUser();

  const [myRosterId,     setMyRosterId]     = useState(null);
  const [rosters,        setRosters]        = useState([]);
  const [draftTeams,     setDraftTeams]     = useState([]);
  const [mode,           setMode]           = useState('realistic');
  const [phase,          setPhase]          = useState('lobby');  // lobby | drafting | complete
  const [session,        setSession]        = useState(null);
  const [board,          setBoard]          = useState([]);
  const [visiblePickNo,  setVisiblePickNo]  = useState(null);   // null = show all
  const [animPickNo,     setAnimPickNo]     = useState(null);   // cell currently flashing
  const [recs,           setRecs]           = useState([]);
  const [avail,          setAvail]          = useState([]);
  const [upcoming,       setUpcoming]       = useState([]);
  const [analysis,       setAnalysis]       = useState(null);
  const [aiLog,          setAiLog]          = useState([]);
  const [tradeModal,     setTradeModal]     = useState(false);
  const [tradeOffer,     setTradeOffer]     = useState({ give: [], receive: [] });
  const [tradeResult,    setTradeResult]    = useState(null);
  const [loading,        setLoading]        = useState(false);
  const [err,            setErr]            = useState(null);
  const [searchTerm,     setSearchTerm]     = useState('');
  const [posFilter,      setPosFilter]      = useState('ALL');
  const [view,           setView]           = useState('recs'); // recs | available | log | analysis | fullboard
  const [aiToast,        setAiToast]        = useState(null);
  const [aiSpeed,        setAiSpeed]        = useState('normal');
  const [myDraftSlot,    setMyDraftSlot]    = useState(null);
  const [isMobile,       setIsMobile]       = useState(() => window.innerWidth < 768);
  const [mobileTab,      setMobileTab]      = useState('picks'); // picks | board | roster
  const [showAvailSheet, setShowAvailSheet] = useState(false);
  const [draftHistory,   setDraftHistory]   = useState([]);
  const [historyDetail,  setHistoryDetail]  = useState(null);  // {id, analysis, picks, ...}
  const [historyLoading, setHistoryLoading] = useState(false);
  const [lobbyHistoryOpen, setLobbyHistoryOpen] = useState(false);
  const [saveState,      setSaveState]      = useState({ saved: false, historyId: null, error: null });

  const boardRef   = useRef(null);
  const aiTimerRef = useRef(null);

  const isCommissioner =
    currentUser?.display_name === 'PhillyxCommish' ||
    currentUser?.username     === 'PhillyxCommish';

  // ── Mobile resize ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  useEffect(() => {
    return () => { if (aiTimerRef.current) clearTimeout(aiTimerRef.current); };
  }, []);

  // ── Load rosters + teams ──────────────────────────────────────────────────────
  useEffect(() => {
    fetch(`${api}/api/lor/rosters`)
      .then(r => r.json())
      .then(data => setRosters(Array.isArray(data) ? data : []))
      .catch(() => {});
    fetch(`${api}/api/lor/mock-draft/teams`)
      .then(r => r.json())
      .then(data => setDraftTeams(data?.teams || []))
      .catch(() => {});
  }, [api]);

  useEffect(() => {
    if (!rosters.length) return;
    const stored = localStorage.getItem('lor_selected_manager') || localStorage.getItem('lor_selected_roster_id');
    const resolvedRosterId = resolveLorRosterId(currentUser, rosters, stored);
    setMyRosterId(resolvedRosterId);
  }, [currentUser, rosters]);

  useEffect(() => {
    if (!myRosterId) return;
    fetch(`${api}/api/lor/mock-draft/slot/${myRosterId}`)
      .then(r => r.json())
      .then(d => { if (d.draft_slot) setMyDraftSlot(d.draft_slot); })
      .catch(() => {});
  }, [api, myRosterId]);

  // ── Restore session ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!myRosterId) return;
    fetch(`${api}/api/lor/mock-draft/session/${myRosterId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && data.status !== 'not_found' && data.status !== 'no_session') {
          const b = data.board_json || [];
          setSession(data);
          setBoard(b);
          setMode(data.mode || 'realistic');
          if (data?.my_draft_slot) setMyDraftSlot(data.my_draft_slot);
          if (data.status === 'complete') {
            setSaveState({
              saved: Boolean(data.saved),
              historyId: data.history_id || null,
              error: data.save_error || null,
            });
            setPhase('complete');
            fetchAnalysis();
            fetchHistory();
          } else {
            setPhase('drafting');
            fetchRecs();
          }
        }
      })
      .catch(() => {});
  }, [myRosterId]); // eslint-disable-line

  // ── Fetch recs + available ────────────────────────────────────────────────────
  const fetchRecs = useCallback(async () => {
    if (!myRosterId) return;
    try {
      const [recsRes, availRes] = await Promise.all([
        fetch(`${api}/api/lor/mock-draft/recommendations/${myRosterId}`),
        fetch(`${api}/api/lor/mock-draft/availability/${myRosterId}`),
      ]);
      if (recsRes.ok) {
        const d = await recsRes.json();
        setRecs(d.recommendations || []);
        setAvail(d.available || []);
      } else {
        const errText = await recsRes.text().catch(() => '');
        console.error('[MockDraft] recommendations failed:', recsRes.status, errText);
        setErr(`Player pool unavailable (${recsRes.status}). Try resetting the draft.`);
      }
      if (availRes.ok) {
        const d = await availRes.json();
        setUpcoming(d.upcoming || []);
      }
    } catch (e) {
      console.error('[MockDraft] fetchRecs error:', e);
    }
  }, [api, myRosterId]);

  const fetchAnalysis = useCallback(async () => {
    if (!myRosterId) return;
    try {
      const r = await fetch(`${api}/api/lor/mock-draft/analysis/${myRosterId}`);
      if (r.ok) {
        const data = await r.json();
        setAnalysis(data);
        setSaveState({
          saved: Boolean(data.saved),
          historyId: data.history_id || null,
          error: data.save_error || null,
        });
      }
    } catch {}
  }, [api, myRosterId]);

  // ── Animate AI picks sequentially ────────────────────────────────────────────
  const _animateAiPicks = useCallback((newAi, finalBoard, onDone) => {
    setBoard(finalBoard);
    if (aiSpeed === 'fast' || !newAi.length) {
      if (newAi.length) {
        setAiLog(prev => [
          ...newAi.map(p => ({
            pick_no: p.pick_no, pick_display: p.pick_display,
            player_name: p.player_name, position: p.position,
            team: p.team, pick_type: p.pick_type,
            display_name: p.display_name || '',
          })),
          ...prev,
        ].slice(0, 60));
      }
      setVisiblePickNo(null);
      setAnimPickNo(null);
      setAiToast(null);
      // Fast mode: defer onDone by one frame so React flushes the setBoard(finalBoard)
      // update before fetchRecs fires. Without this, isMyTurn reads stale board state
      // on the first render after a pick, causing the draft to stall in late rounds.
      if (aiSpeed === 'fast' && newAi.length > 0) {
        setTimeout(onDone, 50);
      } else {
        onDone();
      }
      return;
    }

    // Start with all new AI picks hidden
    const startHide = (newAi[0]?.pick_no || 1) - 1;
    setVisiblePickNo(startHide);

    let idx = 0;
    const step = () => {
      if (idx < newAi.length) {
        const pick = newAi[idx];
        setAiToast(pick);
        setVisiblePickNo(pick.pick_no);
        setAnimPickNo(pick.pick_no);
        setAiLog(prev => [{
          pick_no: pick.pick_no, pick_display: pick.pick_display,
          player_name: pick.player_name, position: pick.position,
          team: pick.team, pick_type: pick.pick_type,
          display_name: pick.display_name || '',
        }, ...prev].slice(0, 60));
        idx++;
        aiTimerRef.current = setTimeout(() => {
          setAnimPickNo(null);
          step();
        }, 900);
      } else {
        setVisiblePickNo(null);
        setAiToast(null);
        onDone();
      }
    };
    step();
  }, [aiSpeed]);

  // ── Start draft ───────────────────────────────────────────────────────────────
  const handleStart = async () => {
    if (!myRosterId) { setErr('Could not find your roster. Make sure you are logged in.'); return; }
    setLoading(true); setErr(null);
    try {
      const r = await fetch(`${api}/api/lor/mock-draft/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roster_id: myRosterId, mode }),
      });
      const d = await r.json();
      if (!r.ok || d.error) { setErr(d.detail || d.error || 'Failed to start draft'); setLoading(false); return; }

      setSession(d);
      setSaveState({ saved: false, historyId: null, error: null });
      if (d.my_draft_slot) setMyDraftSlot(d.my_draft_slot);
      setAiLog([]);
      setPhase('drafting');

      const newAi     = d.ai_picks_made || [];
      const finalBoard = d.board || [];

      _animateAiPicks(newAi, finalBoard, async () => {
        await fetchRecs();
        setLoading(false);
      });
    } catch (e) {
      setErr(String(e));
      setLoading(false);
    }
  };

  // ── Make a pick ───────────────────────────────────────────────────────────────
  const handlePick = async (playerId, recOption = null) => {
    if (!myRosterId || loading) return;
    setLoading(true); setErr(null);
    setShowAvailSheet(false);
    try {
      const r = await fetch(`${api}/api/lor/mock-draft/pick`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roster_id: myRosterId, player_id: playerId, rec_option: recOption }),
      });
      const d = await r.json();
      // Check both HTTP error status and backend error dict (make_pick returns 200 + {error:...})
      if (!r.ok || d.error) { setErr(d.detail || d.error || 'Pick failed'); setLoading(false); return; }

      const newAi      = d.ai_picks_made || [];
      const finalBoard = d.board || [];

      // Update session current_pick so nextSlot logic stays correct
      setSession(prev => prev ? {
        ...prev,
        current_pick: d.current_pick,
        status: d.is_complete ? 'complete' : prev.status,
        saved: d.saved,
        history_id: d.history_id,
        save_error: d.save_error,
      } : prev);

      _animateAiPicks(newAi, finalBoard, async () => {
        if (d.is_complete) {
          setSaveState({
            saved: Boolean(d.saved),
            historyId: d.history_id || null,
            error: d.save_error || null,
          });
          setPhase('complete');
          setView('analysis');
          await fetchAnalysis();
          await fetchHistory();
        } else {
          await fetchRecs();
          if (boardRef.current) {
            const el = boardRef.current.querySelector('[data-next="true"]');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }
        setLoading(false);
      });
    } catch (e) {
      setErr(String(e));
      setLoading(false);
    }
  };

  // ── Trade ─────────────────────────────────────────────────────────────────────
  const handleTradeSubmit = async () => {
    if (!myRosterId || loading) return;
    setLoading(true);
    try {
      const r = await fetch(`${api}/api/lor/mock-draft/trade`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roster_id: myRosterId,
          target_roster_id: tradeOffer.target_roster_id || 1,
          give: tradeOffer.give,
          receive: tradeOffer.receive,
        }),
      });
      const d = await r.json();
      setTradeResult(d);
    } catch (e) {
      setTradeResult({ error: String(e) });
    } finally {
      setLoading(false);
    }
  };

  // ── Reset ─────────────────────────────────────────────────────────────────────
  const handleReset = async () => {
    if (!myRosterId) return;
    if (!window.confirm('Reset your mock draft? All picks will be lost.')) return;
    setLoading(true);
    try {
      const response = await fetch(`${api}/api/lor/mock-draft/session/${myRosterId}`, { method: 'DELETE' });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setErr(data.detail || 'The draft could not be reset safely. Reload and try again.');
        return;
      }
      setBoard([]); setSession(null); setRecs([]); setAvail([]);
      setUpcoming([]); setAnalysis(null); setAiLog([]);
      setVisiblePickNo(null); setAnimPickNo(null); setAiToast(null);
      setSaveState({ saved: false, historyId: null, error: null });
      setErr(null);
      setPhase('lobby');
    } catch {} finally { setLoading(false); }
  };

  // ── History ───────────────────────────────────────────────────────────────────
  const fetchHistory = useCallback(async () => {
    if (!myRosterId) return;
    setHistoryLoading(true);
    try {
      const r = await fetch(`${api}/api/lor/mock-draft/history/${myRosterId}`);
      if (r.ok) {
        const d = await r.json();
        setDraftHistory(d.history || []);
      }
    } catch {} finally { setHistoryLoading(false); }
  }, [api, myRosterId]);

  const fetchHistoryDetail = useCallback(async (draftId) => {
    if (!myRosterId) return;
    setHistoryLoading(true);
    setHistoryDetail(null);
    try {
      const r = await fetch(`${api}/api/lor/mock-draft/history-detail/${draftId}?roster_id=${myRosterId}`);
      if (r.ok) setHistoryDetail(await r.json());
    } catch {} finally { setHistoryLoading(false); }
  }, [api, myRosterId]);

  // ── Derived state ─────────────────────────────────────────────────────────────
  const filteredAvail = avail.filter(p => {
    const matchPos    = posFilter === 'ALL' || p.position === posFilter;
    const matchSearch = !searchTerm || (p.full_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    return matchPos && matchSearch;
  });

  // Board with visibility gate applied (for animation)
  const displayBoard = visiblePickNo !== null
    ? board.map(s => {
        if (
          s.pick_no > visiblePickNo &&
          s.status === 'drafted' &&
          s.pick_type !== 'keeper' &&
          s.pick_type !== 'projected_keeper'
        ) {
          return { ...s, player_id: null, player_name: null, position: null, team: null, status: 'available', pick_type: 'live' };
        }
        return s;
      })
    : board;

  const currentPick  = session?.current_pick || 0;
  const myPicks      = board.filter(s => s.roster_id === myRosterId && s.status === 'drafted' && s.pick_type !== 'keeper');
  // Use final board for game logic (not affected by animation gating)
  const nextSlot     = board.find(s => s.status === 'available');
  const isMyTurn     = phase === 'drafting' && !loading && nextSlot?.roster_id === myRosterId;
  const totalPicks   = board.length;
  const draftedCount = board.filter(s => s.status === 'drafted' && s.pick_type !== 'keeper').length;
  const keeperCount  = board.filter(s => s.pick_type === 'keeper').length;
  const progressPct  = totalPicks > 0 ? Math.round(((draftedCount + keeperCount) / totalPicks) * 100) : 0;

  const rosterMap    = Object.fromEntries(rosters.map(r => [r.roster_id, r]));

  // "On the clock" uses displayBoard so it follows the animation
  const onClockSlot   = phase === 'drafting' ? displayBoard.find(s => s.status === 'available') : null;
  const onClockRoster = onClockSlot ? rosterMap[onClockSlot.roster_id] : null;
  const onClockName   = onClockRoster?.display_name || (onClockSlot ? `Slot ${onClockSlot.slot}` : '');

  // ── Lobby ─────────────────────────────────────────────────────────────────────
  if (phase === 'lobby') {
    return (
      <DraftLobby
        draftTeams={draftTeams}
        myRosterId={myRosterId}
        myDraftSlot={myDraftSlot}
        mode={mode} setMode={setMode}
        aiSpeed={aiSpeed} setAiSpeed={setAiSpeed}
        onStart={handleStart}
        loading={loading} err={err}
        historyOpen={lobbyHistoryOpen}
        history={draftHistory}
        historyLoading={historyLoading}
        historyDetail={historyDetail}
        onToggleHistory={() => {
          const nextOpen = !lobbyHistoryOpen;
          setLobbyHistoryOpen(nextOpen);
          setHistoryDetail(null);
          if (nextOpen) fetchHistory();
        }}
        onSelectHistory={fetchHistoryDetail}
        onBackHistory={() => setHistoryDetail(null)}
      />
    );
  }

  // ── Draft room ────────────────────────────────────────────────────────────────
  return (
    <div style={S.shell}>
      {/* Top bar */}
      <div style={S.topBar}>
        <div style={S.topBarLeft}>
          <span style={S.topBarTitle}>Mock Draft</span>
          <span style={S.modeBadge(mode)}>
            {mode === 'optimized' ? <><Icon emoji="⚡" size={13} style={{ verticalAlign: 'middle', marginRight: 3 }} /> Optimized</> : '🧠 Realistic'}
          </span>
          {phase === 'complete' && (
            <span style={{ ...S.badge, background: 'rgba(46,204,113,0.2)', color: 'var(--green)' }}>
              <Icon emoji="✓" size={13} style={{ verticalAlign: 'middle', marginRight: 3 }} /> Complete
            </span>
          )}
          {phase === 'complete' && saveState.saved && (
            <button
              type="button"
              style={S.savedBadge}
              onClick={() => { setView('history'); setHistoryDetail(null); fetchHistory(); }}
            >
              Saved ✓ — see it under History
            </button>
          )}
        </div>
        <div style={S.topBarCenter}>
          <div style={S.progressBar}>
            <div style={{ ...S.progressFill, width: `${progressPct}%` }} />
          </div>
          <span style={S.progressText}>
            {draftedCount + keeperCount}/{totalPicks} picks · Pick {currentPick + 1}
          </span>
        </div>
        <div style={S.topBarRight}>
          {phase === 'drafting' && (
            <button style={S.btnSecondary} onClick={() => setTradeModal(true)}>↔ Trade</button>
          )}
          <button style={S.btnDanger} onClick={handleReset}>Reset</button>
        </div>
      </div>

      {err && <div style={S.errBanner}>{err}</div>}
      {phase === 'complete' && !saveState.saved && saveState.error && (
        <div style={S.errBanner}>{saveState.error}</div>
      )}

      {/* On-the-clock banner */}
      {phase === 'drafting' && isMyTurn && (
        <div style={S.myTurnBanner}>
          🔥 You're on the clock — {nextSlot?.pick_display}
        </div>
      )}
      {phase === 'drafting' && !isMyTurn && onClockSlot && !aiToast && (
        <div style={S.aiTurnBanner}>
          {onClockName} is on the clock · {onClockSlot.pick_display}
        </div>
      )}

      {/* AI pick toast */}
      {aiToast && (
        <div style={S.aiToastBanner}>
          <span style={{ color: posStyle(aiToast.position)?.color, fontWeight: 700 }}>{aiToast.position}</span>
          {' '}<strong>{aiToast.player_name}</strong>
          <span style={S.aiToastSub}> · {aiToast.display_name || ''} · {aiToast.pick_display}</span>
        </div>
      )}

      {/* Main layout */}
      {isMobile ? (
        <MobileLayout
          view={view} setView={setView}
          mobileTab={mobileTab} setMobileTab={setMobileTab}
          phase={phase}
          displayBoard={displayBoard}
          myRosterId={myRosterId}
          rosters={rosters}
          draftTeams={draftTeams}
          currentPick={currentPick}
          animPickNo={animPickNo}
          boardRef={boardRef}
          recs={recs}
          isMyTurn={isMyTurn}
          onPick={handlePick}
          loading={loading}
          avail={filteredAvail}
          totalAvail={avail.length}
          searchTerm={searchTerm} setSearchTerm={setSearchTerm}
          posFilter={posFilter} setPosFilter={setPosFilter}
          aiLog={aiLog}
          myPicks={myPicks}
          keeperCount={keeperCount}
          upcoming={upcoming}
          analysis={analysis}
          board={board}
          showAvailSheet={showAvailSheet}
          setShowAvailSheet={setShowAvailSheet}
          draftHistory={draftHistory}
          historyLoading={historyLoading}
          historyDetail={historyDetail}
          fetchHistory={fetchHistory}
          fetchHistoryDetail={fetchHistoryDetail}
          setHistoryDetail={setHistoryDetail}
        />
      ) : (
        <DesktopLayout
          view={view} setView={setView}
          phase={phase}
          displayBoard={displayBoard}
          myRosterId={myRosterId}
          rosters={rosters}
          draftTeams={draftTeams}
          currentPick={currentPick}
          animPickNo={animPickNo}
          boardRef={boardRef}
          recs={recs}
          isMyTurn={isMyTurn}
          onPick={handlePick}
          loading={loading}
          avail={filteredAvail}
          totalAvail={avail.length}
          searchTerm={searchTerm} setSearchTerm={setSearchTerm}
          posFilter={posFilter} setPosFilter={setPosFilter}
          aiLog={aiLog}
          myPicks={myPicks}
          keeperCount={keeperCount}
          upcoming={upcoming}
          analysis={analysis}
          board={board}
          showAvailSheet={showAvailSheet}
          setShowAvailSheet={setShowAvailSheet}
          draftHistory={draftHistory}
          historyLoading={historyLoading}
          historyDetail={historyDetail}
          fetchHistory={fetchHistory}
          fetchHistoryDetail={fetchHistoryDetail}
          setHistoryDetail={setHistoryDetail}
        />
      )}

      {/* Trade modal */}
      {tradeModal && (
        <TradeModal
          board={board} myRosterId={myRosterId} rosters={rosters} avail={avail}
          tradeOffer={tradeOffer} setTradeOffer={setTradeOffer}
          tradeResult={tradeResult}
          onSubmit={handleTradeSubmit}
          onClose={() => { setTradeModal(false); setTradeResult(null); setTradeOffer({ give: [], receive: [] }); }}
          loading={loading}
        />
      )}
    </div>
  );
}

// ── Pre-draft lobby ────────────────────────────────────────────────────────────

function DraftLobby({
  draftTeams, myRosterId, myDraftSlot, mode, setMode, aiSpeed, setAiSpeed,
  onStart, loading, err,
  historyOpen, history, historyLoading, historyDetail,
  onToggleHistory, onSelectHistory, onBackHistory,
}) {
  return (
    <div style={S.lobbyWrap}>
      <div style={S.lobbyInner}>

        {/* Header */}
        <div style={S.lobbyHeader}>
          <div style={S.lobbyHeaderIcon}>🏈</div>
          <h2 style={S.lobbyTitle}>Mock Draft</h2>
          <p style={S.lobbySub}>10 teams · 20 rounds · Snake with 3rd-round reversal · Full PPR</p>
        </div>

        {/* Teams grid */}
        {draftTeams.length > 0 && (
          <div style={S.lobbyTeamsSection}>
            <div style={S.lobbySectionLabel}>Draft Order</div>
            <div style={S.lobbyTeamGrid}>
              {draftTeams.map(team => {
                const isMe = team.roster_id === myRosterId;
                return (
                  <div key={team.roster_id} style={{ ...S.lobbyTeamCard, ...(isMe ? S.lobbyTeamCardMe : {}) }}>
                    <div style={S.lobbySlotBadge(isMe)}>{team.slot}</div>
                    <div style={S.lobbyTeamName(isMe)}>
                      {isMe ? 'You' : team.display_name}
                    </div>
                    {isMe && (
                      <div style={S.lobbyYouSub}>{team.display_name}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Two columns: mode + config */}
        <div style={S.lobbyConfigRow}>
          {/* Mode selector */}
          <div style={{ flex: 1 }}>
            <div style={S.lobbySectionLabel}>AI Mode</div>
            <div style={S.modeSelector}>
              {[
                { key: 'realistic',  icon: null,  label: '🧠 Realistic',  desc: "AI uses each manager's 4-season draft profile." },
                { key: 'optimized',  icon: '⚡',  label: 'Optimized',   desc: 'AI drafts strict best-available by ADP.' },
              ].map(m => (
                <button
                  key={m.key}
                  style={{ ...S.modeCard, ...(mode === m.key ? S.modeCardActive : {}) }}
                  onClick={() => setMode(m.key)}
                >
                  <div style={S.modeCardLabel}>{m.icon ? <><Icon emoji={m.icon} size={13} style={{ verticalAlign: 'middle', marginRight: 3 }} /> {m.label}</> : m.label}</div>
                  <div style={S.modeCardDesc}>{m.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Speed */}
          <div style={{ minWidth: 160 }}>
            <div style={S.lobbySectionLabel}>AI Speed</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[
                { key: 'normal', icon: null, label: '🎬 Normal (1s/pick)' },
                { key: 'fast',   icon: '⚡', label: 'Fast (instant)'  },
              ].map(s => (
                <button
                  key={s.key}
                  style={{ ...S.speedBtn, ...(aiSpeed === s.key ? S.speedBtnActive : {}) }}
                  onClick={() => setAiSpeed(s.key)}
                >
                  {s.icon ? <><Icon emoji={s.icon} size={13} style={{ verticalAlign: 'middle', marginRight: 3 }} /> {s.label}</> : s.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Draft slot */}
        {myDraftSlot ? (
          <div style={S.lobbySlotInfo}>
            <Icon emoji="⚡" size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Your slot: <strong>1.{String(myDraftSlot).padStart(2, '0')}</strong> — pick {myDraftSlot} of 10
          </div>
        ) : (
          <div style={{ ...S.lobbySlotInfo, color: '#AAA', borderColor: 'rgba(255,255,255,0.15)' }}>
            Draft order not yet set — you'll be placed in your roster slot
          </div>
        )}

        {/* Features list */}
        <div style={S.lobbyFeatures}>
          {['✓ Keepers pre-slotted on the board', '✓ 3 pick recommendations every turn',
            '✓ Trade proposals with AI response', '✓ Post-draft grades & analysis',
            '✓ Auto-saves — resume anytime'].map((f, i) => (
            <span key={i} style={S.lobbyFeatureItem}>{f}</span>
          ))}
        </div>

        {err && <div style={S.errBanner}>{err}</div>}

        <button
          style={{ ...S.btnPrimary, fontSize: 17, padding: '16px 0', width: '100%', marginTop: 8 }}
          onClick={onStart}
          disabled={loading}
        >
          {loading ? 'Starting Draft...' : '🏈 Start Mock Draft'}
        </button>

        {myRosterId && (
          <div style={S.lobbyHistorySection}>
            <button
              type="button"
              style={S.lobbyHistoryButton}
              onClick={onToggleHistory}
              aria-expanded={historyOpen}
            >
              <span>🕐 Past Mocks / History</span>
              <span aria-hidden="true">{historyOpen ? '▲' : '▼'}</span>
            </button>
            {historyOpen && (
              <div style={S.lobbyHistoryPanel}>
                <HistoryPanel
                  history={history}
                  loading={historyLoading}
                  detail={historyDetail}
                  onSelect={onSelectHistory}
                  onBack={onBackHistory}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Desktop layout ─────────────────────────────────────────────────────────────

function DesktopLayout({
  view, setView, phase,
  displayBoard, myRosterId, rosters, draftTeams, currentPick, animPickNo, boardRef,
  recs, isMyTurn, onPick, loading,
  avail, totalAvail, searchTerm, setSearchTerm, posFilter, setPosFilter,
  aiLog, myPicks, keeperCount, upcoming, analysis, board,
  showAvailSheet, setShowAvailSheet,
  draftHistory, historyLoading, historyDetail,
  fetchHistory, fetchHistoryDetail, setHistoryDetail,
}) {
  return (
    <div style={S.mainLayout}>
      {/* LEFT: Sleeper-style board */}
      <div style={S.boardPanel}>
        <div style={S.panelHeader}>
          <span style={S.panelTitle}>Draft Board</span>
          <span style={S.panelSub}>{board.filter(s => s.status === 'available').length} live picks remaining</span>
        </div>
        <SleeperBoard
          board={displayBoard}
          myRosterId={myRosterId}
          rosters={rosters}
          draftTeams={draftTeams}
          currentPick={currentPick}
          animPickNo={animPickNo}
          boardRef={boardRef}
        />
      </div>

      {/* CENTER: Tabs */}
      <div style={S.centerPanel}>
        <div style={S.viewTabs}>
          {[
            { key: 'recs',      icon: '🎯', label: 'Picks' },
            { key: 'available', icon: '📋', label: 'Available' },
            { key: 'log',       icon: null, label: `📜 AI Log (${aiLog.length})` },
            ...(phase === 'complete' ? [
              { key: 'analysis',  icon: null, label: '📊 Analysis'  },
              { key: 'fullboard', icon: null, label: '🏈 Full Board' },
            ] : []),
            { key: 'history', icon: null, label: '🕐 History' },
          ].map(t => (
            <button
              key={t.key}
              style={{ ...S.viewTab, ...(view === t.key ? S.viewTabActive : {}) }}
              onClick={() => {
                setView(t.key);
                if (t.key === 'history') { setHistoryDetail(null); fetchHistory(); }
              }}
            >
              {t.icon ? <><Icon emoji={t.icon} size={13} style={{ verticalAlign: 'middle', marginRight: 3 }} /> {t.label}</> : t.label}
            </button>
          ))}
        </div>

        {view === 'recs' && (
          <PickCards
            recs={recs} isMyTurn={isMyTurn} onPick={onPick} loading={loading}
            myPicks={myPicks} upcoming={upcoming}
            onBrowse={() => setView('available')}
          />
        )}
        {view === 'available' && (
          <AvailablePanel
            avail={avail} totalAvail={totalAvail} isMyTurn={isMyTurn} onPick={onPick} loading={loading}
            searchTerm={searchTerm} setSearchTerm={setSearchTerm}
            posFilter={posFilter} setPosFilter={setPosFilter}
          />
        )}
        {view === 'log' && <AiLogPanel aiLog={aiLog} />}
        {view === 'analysis' && analysis && <AnalysisPanel analysis={analysis} myPicks={myPicks} />}
        {view === 'fullboard' && <FullBoardPanel board={board} myRosterId={myRosterId} rosters={rosters} draftTeams={draftTeams} />}
        {view === 'history' && (
          <HistoryPanel
            history={draftHistory} loading={historyLoading}
            detail={historyDetail} onSelect={fetchHistoryDetail} onBack={() => setHistoryDetail(null)}
          />
        )}
      </div>

      {/* RIGHT: Upcoming + roster */}
      <div style={S.rightPanel}>
        <div style={S.panelHeader}>
          <span style={S.panelTitle}>My Upcoming</span>
        </div>
        <UpcomingPanel upcoming={upcoming} />
        <div style={{ ...S.panelHeader, marginTop: 16 }}>
          <span style={S.panelTitle}>My Roster ({myPicks.length + keeperCount})</span>
        </div>
        <MyRosterPanel board={board} myRosterId={myRosterId} />
      </div>
    </div>
  );
}

// ── Mobile layout ──────────────────────────────────────────────────────────────

function MobileLayout({
  view, setView, phase,
  mobileTab, setMobileTab,
  displayBoard, myRosterId, rosters, draftTeams, currentPick, animPickNo, boardRef,
  recs, isMyTurn, onPick, loading,
  avail, totalAvail, searchTerm, setSearchTerm, posFilter, setPosFilter,
  aiLog, myPicks, keeperCount, upcoming, analysis, board,
  showAvailSheet, setShowAvailSheet,
  draftHistory, historyLoading, historyDetail,
  fetchHistory, fetchHistoryDetail, setHistoryDetail,
}) {
  return (
    <div style={S.mobileShell}>
      <div style={S.mobileContent}>
        {mobileTab === 'picks' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
            <div style={S.viewTabs}>
              {[
                { key: 'recs',      icon: '🎯', label: 'Picks' },
                { key: 'available', icon: '📋', label: 'Players' },
                { key: 'log',       icon: null, label: '📜 Log' },
                ...(phase === 'complete' ? [
                  { key: 'analysis',  icon: null, label: '📊 Grade'  },
                  { key: 'fullboard', icon: null, label: '🏈 Board'  },
                ] : []),
                { key: 'history',   icon: null, label: '🕐 History' },
              ].map(t => (
                <button
                  key={t.key}
                  style={{ ...S.viewTab, ...(view === t.key ? S.viewTabActive : {}) }}
                  onClick={() => {
                    setView(t.key);
                    if (t.key === 'history') { setHistoryDetail(null); fetchHistory(); }
                  }}
                >
                  {t.icon ? <><Icon emoji={t.icon} size={13} style={{ verticalAlign: 'middle', marginRight: 3 }} /> {t.label}</> : t.label}
                </button>
              ))}
            </div>
            <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {view === 'recs' && (
                <PickCards
                  recs={recs} isMyTurn={isMyTurn} onPick={onPick} loading={loading}
                  myPicks={myPicks} upcoming={upcoming}
                  onBrowse={() => setView('available')}
                />
              )}
              {view === 'available' && (
                <AvailablePanel
                  avail={avail} totalAvail={totalAvail} isMyTurn={isMyTurn} onPick={onPick} loading={loading}
                  searchTerm={searchTerm} setSearchTerm={setSearchTerm}
                  posFilter={posFilter} setPosFilter={setPosFilter}
                />
              )}
              {view === 'log' && <AiLogPanel aiLog={aiLog} />}
              {view === 'analysis' && analysis && <AnalysisPanel analysis={analysis} myPicks={myPicks} />}
              {view === 'fullboard' && <FullBoardPanel board={board} myRosterId={myRosterId} rosters={rosters} draftTeams={draftTeams} />}
              {view === 'history' && (
                <HistoryPanel
                  history={draftHistory} loading={historyLoading}
                  detail={historyDetail} onSelect={fetchHistoryDetail}
                  onBack={() => setHistoryDetail(null)}
                />
              )}
            </div>
          </div>
        )}

        {mobileTab === 'board' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
            <div style={S.panelHeader}>
              <span style={S.panelTitle}>Draft Board</span>
              <span style={S.panelSub}>{board.filter(s => s.status === 'available').length} remaining</span>
            </div>
            <SleeperBoard
              board={displayBoard}
              myRosterId={myRosterId}
              rosters={rosters}
              draftTeams={draftTeams}
              currentPick={currentPick}
              animPickNo={animPickNo}
              boardRef={boardRef}
            />
          </div>
        )}

        {mobileTab === 'roster' && (
          <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 16 }}>
            <div style={S.panelHeader}>
              <span style={S.panelTitle}>My Roster ({myPicks.length + keeperCount})</span>
            </div>
            <MyRosterPanel board={board} myRosterId={myRosterId} />
            <div style={{ ...S.panelHeader, marginTop: 12 }}>
              <span style={S.panelTitle}>Upcoming Picks</span>
            </div>
            <UpcomingPanel upcoming={upcoming} />
          </div>
        )}
      </div>

      {/* Mobile bottom nav */}
      <div style={S.mobileNav}>
        {[
          { key: 'picks',  emoji: '🎯', label: 'Picks'  },
          { key: 'board',  emoji: '📋', label: 'Board'  },
          { key: 'roster', emoji: '👤', label: 'Roster' },
        ].map(t => (
          <button
            key={t.key}
            style={{ ...S.mobileNavBtn, ...(mobileTab === t.key ? S.mobileNavBtnActive : {}) }}
            onClick={() => setMobileTab(t.key)}
          >
            <span style={{ fontSize: 22 }}><Icon emoji={t.emoji} size={22} /></span>
            <span style={{ fontSize: 10, marginTop: 2 }}>{t.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Sleeper-style draft board (teams × rounds grid) ────────────────────────────

function SleeperBoard({ board, myRosterId, rosters, draftTeams, currentPick, animPickNo, boardRef }) {
  const rounds = 20;

  // Build slot order from draftTeams (already sorted by slot)
  // Fall back to extracting order from board if draftTeams not loaded
  let teamOrder;
  if (draftTeams.length > 0) {
    teamOrder = draftTeams.map(t => ({ rosterId: t.roster_id, displayName: t.display_name, slot: t.slot }));
  } else {
    const seen = new Set();
    const bySlot = [];
    const slots = [...board].sort((a, b) => a.pick_no - b.pick_no);
    for (const s of slots) {
      if (!seen.has(s.slot)) {
        seen.add(s.slot);
        const r = rosters.find(r => r.roster_id === s.roster_id);
        bySlot.push({ rosterId: s.roster_id, displayName: r?.display_name || `Slot ${s.slot}`, slot: s.slot });
      }
    }
    teamOrder = bySlot.sort((a, b) => a.slot - b.slot);
  }

  // Index board: { roster_id: { round: slot_obj } }
  const grid = {};
  for (const s of board) {
    if (!grid[s.roster_id]) grid[s.roster_id] = {};
    grid[s.roster_id][s.round] = s;
  }

  const nextAvailable = board.find(s => s.status === 'available');

  return (
    <div style={S.sleeperBoardOuter} ref={boardRef}>
      <div style={S.sleeperBoardScroll}>
        {/* Header row: round numbers */}
        <div style={S.sleeperHeaderRow}>
          <div style={S.sleeperTeamCol} />
          {Array.from({ length: rounds }, (_, i) => i + 1).map(rnd => (
            <div key={rnd} style={S.sleeperRndHeader}>R{rnd}</div>
          ))}
        </div>

        {/* Team rows */}
        {teamOrder.map(team => {
          const isMe = team.rosterId === myRosterId;
          return (
            <div key={team.rosterId} style={{ ...S.sleeperTeamRow, ...(isMe ? S.sleeperTeamRowMe : {}) }}>
              {/* Sticky team name */}
              <div style={{ ...S.sleeperTeamCol, ...(isMe ? S.sleeperTeamColMe : {}) }}>
                <span style={S.sleeperTeamSlot(isMe)}>{team.slot}</span>
                <span style={S.sleeperTeamName(isMe)}>
                  {isMe ? 'You' : team.displayName}
                </span>
              </div>

              {/* Round cells */}
              {Array.from({ length: rounds }, (_, i) => i + 1).map(rnd => {
                const slot = grid[team.rosterId]?.[rnd];
                if (!slot) return <div key={rnd} style={S.sleeperCell} />;

                const isNext   = slot.pick_no === (nextAvailable?.pick_no);
                const isAnim   = slot.pick_no === animPickNo;
                const isKeeper = slot.pick_type === 'keeper';
                const isProjK  = slot.pick_type === 'projected_keeper';
                const ps       = slot.position ? posStyle(slot.position) : null;

                return (
                  <div
                    key={rnd}
                    data-next={isNext || undefined}
                    style={{
                      ...S.sleeperCell,
                      ...(isMe ? S.sleeperCellMe : {}),
                      ...(isNext ? S.sleeperCellNext : {}),
                      ...(isKeeper ? S.sleeperCellKeeper : {}),
                      ...(isProjK  ? S.sleeperCellProjK  : {}),
                      ...(isAnim   ? S.sleeperCellAnim   : {}),
                    }}
                  >
                    <div style={S.sleeperCellPick}>{slot.pick_display}</div>
                    {slot.player_name ? (
                      <div style={{ animation: isAnim ? 'land-in 0.3s ease' : undefined }}>
                        {ps && (
                          <div style={{ ...S.sleeperCellPos, color: ps.color, background: ps.bg }}>
                            {slot.position}
                          </div>
                        )}
                        <div style={S.sleeperCellName}>
                          {slot.player_name.split(' ').pop()}
                        </div>
                        {isKeeper && <div style={S.keeperBadge}>K</div>}
                        {isProjK  && <div style={S.projKeeperBadge}>P</div>}
                      </div>
                    ) : (
                      <div style={S.sleeperCellEmpty}>{isNext ? '▼' : ''}</div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Pick recommendation cards ──────────────────────────────────────────────────

const REC_META = [
  { emoji: '🏆', label: 'Best ADP Available' },
  { emoji: '🎯', label: 'Best Positional Fit' },
  { emoji: '⚡', label: 'Value Pick'          },
];

function PickCards({ recs, isMyTurn, onPick, loading, myPicks, upcoming, onBrowse }) {
  return (
    <div style={S.pickCardsWrap}>
      {!isMyTurn && recs.length > 0 && (
        <div style={S.recsSectionLabel}><Icon emoji="📋" size={13} style={{ verticalAlign: 'middle', marginRight: 3 }} /> Projected for your next turn</div>
      )}
      {isMyTurn && recs.length > 0 && (
        <div style={{ ...S.recsSectionLabel, color: 'var(--lor-gold)' }}><Icon emoji="⚡" size={13} style={{ verticalAlign: 'middle', marginRight: 3 }} /> You're on the clock — tap to draft</div>
      )}

      {recs.length === 0 && (
        <div style={S.emptyState}>
          {isMyTurn && !loading
            ? 'Recommendations unavailable — use Browse to pick manually.'
            : loading ? 'AI is picking...' : 'Waiting for your next turn...'}
        </div>
      )}

      <div style={S.pickCardList}>
        {recs.map((rec, idx) => {
          const p  = rec.player;
          const ps = posStyle(p.position);
          const meta = REC_META[idx] || { emoji: '•', label: rec.label };
          return (
            <button
              key={rec.option}
              style={{ ...S.pickCard, ...(isMyTurn ? S.pickCardActive : {}), outline: 'none' }}
              onClick={() => isMyTurn && !loading && onPick(p.player_id, rec.option)}
              disabled={!isMyTurn || loading}
            >
              <div style={S.pickCardTop}>
                <span style={S.pickCardEmoji}><Icon emoji={meta.emoji} size={22} /></span>
                <span style={S.pickCardLabel}>{meta.label}</span>
                <span style={{ ...S.riskBadge, color: RISK_BADGE[rec.risk]?.color }}>
                  {RISK_BADGE[rec.risk]?.label}
                </span>
              </div>
              <div style={S.pickCardPlayer}>
                <div style={{ ...S.pickCardPos, color: ps.color, background: ps.bg }}>
                  {p.position}
                </div>
                <div style={S.pickCardInfo}>
                  <div style={S.pickCardName}>{p.full_name}</div>
                  <div style={S.pickCardSub}>
                    {p.team || 'FA'} · ADP {p.adp_pick_no ? `#${Math.round(p.adp_pick_no)}` : 'N/A'}
                    {p.injury_status && <span style={S.injuryBadge}> · {p.injury_status}</span>}
                  </div>
                </div>
              </div>
              <div style={S.pickCardReason}>{rec.reasoning}</div>
              {isMyTurn && (
                <div style={S.pickCardDraftBtn}>
                  {loading ? 'Drafting...' : `Draft ${p.full_name.split(' ').pop()}`}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {isMyTurn && (
        <button style={S.browseBtn} onClick={onBrowse}>Browse All Players →</button>
      )}

      {upcoming.length > 0 && (
        <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={S.recsSectionLabel}>🔭 Upcoming picks availability</div>
          {upcoming.map((slot, i) => (
            <div key={i} style={S.upcomingCard}>
              <div style={S.upcomingHeader}>
                Pick {slot.pick_display}
                <span style={S.upcomingDist}>{slot.picks_until} picks away</span>
              </div>
              {slot.at_risk_players?.length > 0 && (
                <div style={S.upcomingSection}>
                  <div style={S.upcomingLabel}>⚠ At risk:</div>
                  {slot.at_risk_players.map((p, j) => (
                    <span key={j} style={{ ...S.upcomingPlayer, color: posStyle(p.position).color }}>
                      {p.full_name} ({p.position})
                    </span>
                  ))}
                </div>
              )}
              {slot.likely_still_available?.length > 0 && (
                <div style={S.upcomingSection}>
                  <div style={S.upcomingLabel}><Icon emoji="✓" size={12} style={{ verticalAlign: 'middle', marginRight: 3 }} /> Likely available:</div>
                  {slot.likely_still_available.map((p, j) => (
                    <span key={j} style={{ ...S.upcomingPlayer, color: '#95A5A6' }}>
                      {p.full_name} ({p.position})
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Available players panel ────────────────────────────────────────────────────

function AvailablePanel({ avail, totalAvail, isMyTurn, onPick, loading, searchTerm, setSearchTerm, posFilter, setPosFilter }) {
  return (
    <div style={S.availWrap}>
      <div style={S.filterRow}>
        <input
          style={S.searchInput}
          placeholder="Search player..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
        <div style={S.posFilters}>
          {['ALL', 'QB', 'RB', 'WR', 'TE', 'K', 'DEF'].map(pos => (
            <button
              key={pos}
              style={{
                ...S.posFilterBtn,
                ...(posFilter === pos ? S.posFilterBtnActive : {}),
                ...(pos !== 'ALL' ? { color: posStyle(pos).color } : {}),
              }}
              onClick={() => setPosFilter(pos)}
            >
              {pos}
            </button>
          ))}
        </div>
      </div>

      <div style={S.availList}>
        {avail.slice(0, 100).map((p, i) => {
          const ps = posStyle(p.position);
          return (
            <div key={p.player_id} style={S.availRow}>
              <span style={S.availRank}>#{i + 1}</span>
              <span style={{ ...S.availPos, color: ps.color, background: ps.bg }}>{p.position}</span>
              <div style={S.availInfo}>
                <span style={S.availName}>{p.full_name}</span>
                <span style={S.availSub}>
                  {p.team || 'FA'}
                  {p.adp_pick_no ? ` · ADP #${Math.round(p.adp_pick_no)}` : ''}
                  {p.injury_status && <span style={S.injuryBadge}> · {p.injury_status}</span>}
                </span>
              </div>
              {isMyTurn && (
                <button
                  style={S.availPickBtn}
                  onClick={() => onPick(p.player_id)}
                  disabled={loading}
                >
                  Draft
                </button>
              )}
            </div>
          );
        })}
        {avail.length === 0 && (
          <div style={S.emptyState}>
            {totalAvail === 0
              ? 'Player list is loading...'
              : 'No players match your filter.'}
          </div>
        )}
      </div>
    </div>
  );
}

// ── AI log panel ───────────────────────────────────────────────────────────────

function AiLogPanel({ aiLog }) {
  return (
    <div style={S.logWrap}>
      {aiLog.length === 0 && (
        <div style={S.emptyState}>AI picks will appear here as the draft progresses.</div>
      )}
      {aiLog.map((p, i) => {
        const ps = posStyle(p.position);
        const rnd = p.pick_no ? Math.ceil(p.pick_no / 10) : '?';
        return (
          <div key={i} style={S.logRow}>
            <span style={S.logPickNo}>{p.pick_display}</span>
            <span style={{ ...S.logPos, color: ps.color }}>{p.position}</span>
            <div style={S.logBody}>
              <span style={S.logManager}>{p.display_name || `Pick ${p.pick_no}`}</span>
              <span style={S.logName}>{p.player_name}</span>
              <span style={S.logTeam}>{p.team || ''}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Upcoming picks sidebar ─────────────────────────────────────────────────────

function UpcomingPanel({ upcoming }) {
  if (!upcoming.length) return (
    <div style={S.emptyState}>Your picks will appear here once the draft starts.</div>
  );
  return (
    <div style={S.upcomingSidebar}>
      {upcoming.map((slot, i) => (
        <div key={i} style={S.upcomingSidebarCard}>
          <div style={S.upcomingSidebarPick}>{slot.pick_display}</div>
          <div style={S.upcomingSidebarDist}>{slot.picks_until} away</div>
        </div>
      ))}
    </div>
  );
}

// ── My roster panel ────────────────────────────────────────────────────────────

function MyRosterPanel({ board, myRosterId }) {
  const mySlots = board
    .filter(s => s.roster_id === myRosterId && s.player_id)
    .sort((a, b) => a.pick_no - b.pick_no);

  const byPos = {};
  for (const s of mySlots) {
    const pos = s.position || '?';
    if (!byPos[pos]) byPos[pos] = [];
    byPos[pos].push(s);
  }

  return (
    <div style={S.myRosterWrap}>
      {['QB', 'RB', 'WR', 'TE', 'K', 'DEF', '?'].map(pos => {
        const players = byPos[pos];
        if (!players?.length) return null;
        const ps = posStyle(pos);
        return (
          <div key={pos}>
            <div style={{ ...S.rosterPosHeader, color: ps.color }}>{pos} ({players.length})</div>
            {players.map((s, i) => (
              <div key={i} style={S.rosterRow}>
                <span style={S.rosterPickDisp}>{s.pick_display}</span>
                <span style={S.rosterName}>{s.player_name}</span>
                {s.pick_type === 'keeper' && <span style={S.keeperMini}>K</span>}
                {s.grade && s.pick_type !== 'keeper' && (
                  <span style={{ color: GRADE_STYLES[s.grade]?.color, fontSize: 10 }}>
                    {GRADE_STYLES[s.grade]?.label}
                  </span>
                )}
              </div>
            ))}
          </div>
        );
      })}
      {mySlots.length === 0 && <div style={S.emptyState}>Your drafted players appear here.</div>}
    </div>
  );
}

// ── Post-draft analysis ────────────────────────────────────────────────────────

function AnalysisPanel({ analysis, myPicks }) {
  const [expandedPick, setExpandedPick] = useState(null);
  const { picks_graded, positional_totals, positional_gaps, biggest_reach, best_value, reach_count, value_count, fair_count } = analysis;
  const gc = gradeColor(analysis.overall_grade);

  return (
    <div style={S.analysisWrap}>
      {/* Grade hero */}
      <div style={S.gradeHero}>
        <div style={{ ...S.gradeCircle, borderColor: gc, color: gc }}>
          {analysis.overall_grade}
        </div>
        <div style={S.gradeHeroRight}>
          <div style={S.gradeHeroTitle}>Draft Grade</div>
          <div style={S.gradeSummaryRow}>
            <span style={{ color: 'var(--green)' }}>{value_count} values</span>
            <span style={{ color: '#95A5A6' }}>{fair_count} fair</span>
            <span style={{ color: 'var(--red)' }}>{reach_count} reaches</span>
          </div>
          <div style={S.tierGradesRow}>
            {[
              { key: 'early', label: 'R1-3' }, { key: 'mid', label: 'R4-8' },
              { key: 'late', label: 'R9+' },
            ].map(t => {
              const g = analysis.tier_grades?.[t.key];
              if (!g) return null;
              const c = gradeColor(g);
              return (
                <div key={t.key} style={{ ...S.tierGradeCard, borderColor: c + '60' }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: c }}>{g}</div>
                  <div style={{ fontSize: 9, color: '#AAA', marginTop: 1 }}>{t.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Strategy narrative */}
      {analysis.strategy_summary && (
        <div style={{ ...S.keyRecCard, marginBottom: 12 }}>
          <div style={S.keyRecLabel}>📝 Draft Summary</div>
          <div style={{ ...S.keyRecText, lineHeight: 1.6 }}>{analysis.strategy_summary}</div>
        </div>
      )}

      {/* Round-by-round grades */}
      {analysis.round_grades && Object.keys(analysis.round_grades).length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={S.allPicksLabel}>Round Grades</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
            {Object.entries(analysis.round_grades).sort((a, b) => +a[0] - +b[0]).map(([rnd, g]) => {
              if (!g) return null;
              const c = gradeColor(g);
              return (
                <div key={rnd} style={{ ...S.tierGradeCard, borderColor: c + '60', minWidth: 44 }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: c }}>{g}</div>
                  <div style={{ fontSize: 9, color: '#AAA', marginTop: 1 }}>Rd {rnd}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Positional totals */}
      <div style={S.posTotalsRow}>
        {Object.entries(positional_totals || {}).map(([pos, cnt]) => {
          const ps = posStyle(pos);
          return (
            <div key={pos} style={{ ...S.posTotalCard, color: ps.color, borderColor: ps.color + '60' }}>
              <div style={S.posTotalNum}>{cnt}</div>
              <div style={S.posTotalPos}>{pos}</div>
            </div>
          );
        })}
      </div>

      {positional_gaps?.length > 0 && (
        <div style={S.gapWarning}>⚠ Depth gaps: {positional_gaps.join(', ')}</div>
      )}
      {analysis.roster_strengths?.length > 0 && (
        <div style={{ ...S.gapWarning, background: 'rgba(46,204,113,0.08)', borderColor: 'rgba(46,204,113,0.3)', color: 'var(--green)' }}>
          <Icon emoji="✓" size={12} style={{ verticalAlign: 'middle', marginRight: 3 }} /> Strengths: {analysis.roster_strengths.join(', ')}
        </div>
      )}

      {/* Highlights */}
      {best_value && (
        <div style={S.highlightCard}>
          <div style={S.highlightLabel}><Icon emoji="🏆" size={13} style={{ verticalAlign: 'middle', marginRight: 3 }} /> Best Value Pick</div>
          <div style={S.highlightName}>{best_value.player_name}</div>
          <div style={S.highlightSub}>
            Pick {best_value.pick_display} · ADP #{best_value.adp_pick_no ? Math.round(best_value.adp_pick_no) : '?'}
            {best_value.delta !== null && ` · ${Math.abs(best_value.delta)} spots early`}
          </div>
        </div>
      )}
      {biggest_reach && (
        <div style={{ ...S.highlightCard, borderColor: 'rgba(231,76,60,0.4)' }}>
          <div style={{ ...S.highlightLabel, color: 'var(--red)' }}>⚠ Biggest Reach</div>
          <div style={S.highlightName}>{biggest_reach.player_name}</div>
          <div style={S.highlightSub}>
            Pick {biggest_reach.pick_display} · ADP #{biggest_reach.adp_pick_no ? Math.round(biggest_reach.adp_pick_no) : '?'}
            {biggest_reach.delta !== null && ` · ${biggest_reach.delta} spots late`}
          </div>
          {biggest_reach.should_have_done && (
            <div style={{ fontSize: 11, color: '#CCC', marginTop: 6, lineHeight: 1.4 }}>{biggest_reach.should_have_done}</div>
          )}
        </div>
      )}
      {analysis.key_recommendation && (
        <div style={S.keyRecCard}>
          <div style={S.keyRecLabel}><Icon emoji="🎯" size={13} style={{ verticalAlign: 'middle', marginRight: 3 }} /> Key Recommendation</div>
          <div style={S.keyRecText}>{analysis.key_recommendation}</div>
        </div>
      )}

      {/* Per-pick list (expandable) */}
      <div style={S.allPicksLabel}>Pick-by-Pick Breakdown</div>
      <div style={S.allPicksList}>
        {(picks_graded || []).map((p, i) => {
          const ps   = posStyle(p.position);
          const grS  = GRADE_STYLES[p.grade] || GRADE_STYLES.fair;
          const open = expandedPick === i;
          return (
            <div key={i} style={{ ...S.gradedRow, flexDirection: 'column', alignItems: 'flex-start', cursor: 'pointer', paddingBottom: open ? 10 : 4 }}
              onClick={() => setExpandedPick(open ? null : i)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%' }}>
                <span style={S.gradedPickDisp}>{p.pick_display}</span>
                <span style={{ ...S.gradedPos, color: ps.color }}>{p.position}</span>
                <span style={S.gradedName}>{p.player_name}</span>
                {p.was_recommended && (
                  <span style={{ fontSize: 9, color: 'var(--lor-gold)', background: 'rgba(var(--lor-accent-rgb),0.12)', borderRadius: 4, padding: '1px 5px' }}>REC</span>
                )}
                <span style={{ color: grS.color, fontSize: 11, marginLeft: 'auto' }}>{grS.label}</span>
                {p.delta !== null && (
                  <span style={{ color: '#888', fontSize: 11, marginLeft: 4 }}>
                    ({p.delta > 0 ? `+${p.delta}` : p.delta})
                  </span>
                )}
              </div>
              {open && (
                <div style={{ marginTop: 8, paddingLeft: 8, borderLeft: '2px solid rgba(255,255,255,0.08)', width: '100%' }}>
                  {p.should_have_done && (
                    <div style={{ fontSize: 11, color: '#CCC', marginBottom: 6, lineHeight: 1.5 }}>{p.should_have_done}</div>
                  )}
                  {p.best_alternative && (
                    <div style={{ fontSize: 11, color: '#888' }}>
                      Best alt: <span style={{ color: posStyle(p.best_alternative.position).color }}>
                        {p.best_alternative.full_name}
                      </span> ({p.best_alternative.position}) · ADP #{p.best_alternative.adp_pick_no ? Math.round(p.best_alternative.adp_pick_no) : '?'}
                    </div>
                  )}
                  {p.recommendation_type && (
                    <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>
                      Recommendation followed: {p.recommendation_type.replace(/_/g, ' ')}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Draft history panel ────────────────────────────────────────────────────────

function HistoryPanel({ history, loading, detail, onSelect, onBack }) {
  if (loading) return <div style={S.emptyState}>Loading history…</div>;

  if (detail) {
    const a = detail.analysis || {};
    const gc = gradeColor(a.overall_grade);
    return (
      <div style={S.analysisWrap}>
        <button style={{ ...S.btnSecondary, marginBottom: 12 }} onClick={onBack}>← Back to history</button>
        <div style={S.gradeHero}>
          <div style={{ ...S.gradeCircle, borderColor: gc, color: gc }}>{a.overall_grade || '?'}</div>
          <div style={S.gradeHeroRight}>
            <div style={S.gradeHeroTitle}>{detail.display_name} — {detail.mode} mode</div>
            <div style={{ fontSize: 11, color: '#888' }}>{detail.completed_at ? new Date(detail.completed_at).toLocaleDateString() : '—'}</div>
            <div style={S.gradeSummaryRow}>
              <span style={{ color: 'var(--green)' }}>{a.value_count || 0} values</span>
              <span style={{ color: '#95A5A6' }}>{a.fair_count || 0} fair</span>
              <span style={{ color: 'var(--red)' }}>{a.reach_count || 0} reaches</span>
            </div>
          </div>
        </div>
        {a.strategy_summary && (
          <div style={{ ...S.keyRecCard, marginBottom: 12 }}>
            <div style={S.keyRecLabel}>📝 Draft Summary</div>
            <div style={{ ...S.keyRecText, lineHeight: 1.6 }}>{a.strategy_summary}</div>
          </div>
        )}
        <div style={S.allPicksLabel}>Picks</div>
        <div style={S.allPicksList}>
          {(a.picks_graded || detail.picks || []).map((p, i) => {
            const ps  = posStyle(p.position);
            const grS = GRADE_STYLES[p.grade] || GRADE_STYLES.fair;
            return (
              <div key={i} style={S.gradedRow}>
                <span style={S.gradedPickDisp}>{p.pick_display || `R${p.round}`}</span>
                <span style={{ ...S.gradedPos, color: ps.color }}>{p.position}</span>
                <span style={S.gradedName}>{p.player_name}</span>
                <span style={{ color: grS.color, fontSize: 11 }}>{grS.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (!history.length) return (
    <div style={S.emptyState}>
      No saved mocks yet. Complete one to see it here.
    </div>
  );

  return (
    <div style={S.analysisWrap}>
      <div style={S.allPicksLabel}>Past Mock Drafts</div>
      {history.map(d => {
        const gc = gradeColor(d.overall_grade);
        return (
          <div key={d.id}
            style={{ ...S.highlightCard, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}
            onClick={() => onSelect(d.id)}>
            <div style={{ ...S.gradeCircle, width: 40, height: 40, fontSize: 18, borderColor: gc, color: gc, flexShrink: 0 }}>
              {d.overall_grade || '?'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#EEE' }}>
                {d.mode === 'realistic' ? 'Realistic' : 'Optimized'} Draft
              </div>
              <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                {d.completed_at ? new Date(d.completed_at).toLocaleDateString() : '—'} · Slot {d.draft_slot || '?'}
              </div>
              {d.strategy_summary && (
                <div style={{ fontSize: 11, color: '#AAA', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {d.strategy_summary.slice(0, 80)}…
                </div>
              )}
            </div>
            <span style={{ fontSize: 16, color: '#888' }}>›</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Full board (post-draft) ────────────────────────────────────────────────────

function FullBoardPanel({ board, myRosterId, rosters, draftTeams }) {
  const [showMineOnly, setShowMineOnly] = useState(false);

  let teamOrder;
  if (draftTeams.length > 0) {
    teamOrder = draftTeams.map(t => ({ rosterId: t.roster_id, displayName: t.display_name, slot: t.slot }));
  } else {
    const seen = new Set();
    const bySlot = [];
    for (const s of [...board].sort((a, b) => a.pick_no - b.pick_no)) {
      if (!seen.has(s.slot)) {
        seen.add(s.slot);
        const r = rosters.find(r => r.roster_id === s.roster_id);
        bySlot.push({ rosterId: s.roster_id, displayName: r?.display_name || `Slot ${s.slot}`, slot: s.slot });
      }
    }
    teamOrder = bySlot.sort((a, b) => a.slot - b.slot);
  }

  const rounds = 20;
  const grid = {};
  for (const s of board) {
    if (!grid[s.roster_id]) grid[s.roster_id] = {};
    grid[s.roster_id][s.round] = s;
  }

  return (
    <div style={S.fullBoardWrap}>
      <div style={S.fullBoardToolbar}>
        <span style={S.fullBoardTitle}>200-Pick Board</span>
        <button style={S.fullBoardToggleBtn} onClick={() => setShowMineOnly(v => !v)}>
          {showMineOnly ? 'Show All' : 'My Picks Only'}
        </button>
      </div>
      <div style={S.sleeperBoardOuter}>
        <div style={S.sleeperBoardScroll}>
          {/* Header */}
          <div style={S.sleeperHeaderRow}>
            <div style={{ ...S.sleeperTeamCol, fontSize: 9, color: '#888' }} />
            {Array.from({ length: rounds }, (_, i) => i + 1).map(rnd => (
              <div key={rnd} style={S.sleeperRndHeader}>R{rnd}</div>
            ))}
          </div>
          {/* Rows */}
          {teamOrder.map(team => {
            const isMe = team.rosterId === myRosterId;
            if (showMineOnly && !isMe) return null;
            return (
              <div key={team.rosterId} style={{ ...S.sleeperTeamRow, ...(isMe ? S.sleeperTeamRowMe : {}) }}>
                <div style={{ ...S.sleeperTeamCol, ...(isMe ? S.sleeperTeamColMe : {}) }}>
                  <span style={S.sleeperTeamSlot(isMe)}>{team.slot}</span>
                  <span style={S.sleeperTeamName(isMe)}>
                    {isMe ? 'You' : team.displayName}
                  </span>
                </div>
                {Array.from({ length: rounds }, (_, i) => i + 1).map(rnd => {
                  const slot    = grid[team.rosterId]?.[rnd];
                  if (!slot) return <div key={rnd} style={S.sleeperCell} />;
                  const isKeeper = slot.pick_type === 'keeper';
                  const isProjK  = slot.pick_type === 'projected_keeper';
                  const ps       = slot.position ? posStyle(slot.position) : null;
                  return (
                    <div key={rnd} style={{
                      ...S.sleeperCell,
                      ...(isMe ? S.sleeperCellMe : {}),
                      ...(isKeeper ? S.sleeperCellKeeper : {}),
                      ...(isProjK  ? S.sleeperCellProjK  : {}),
                    }}>
                      <div style={S.sleeperCellPick}>{slot.pick_display}</div>
                      {slot.player_name ? (
                        <>
                          {ps && <div style={{ ...S.sleeperCellPos, color: ps.color, background: ps.bg }}>{slot.position}</div>}
                          <div style={S.sleeperCellName}>{slot.player_name.split(' ').pop()}</div>
                          {isKeeper && <div style={S.keeperBadge}>K</div>}
                        </>
                      ) : <div style={S.sleeperCellEmpty} />}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Trade modal ────────────────────────────────────────────────────────────────

function TradeModal({ board, myRosterId, rosters, avail, tradeOffer, setTradeOffer, tradeResult, onSubmit, onClose, loading }) {
  const otherRosters = rosters.filter(r => r.roster_id !== myRosterId);
  const myDrafted    = board.filter(s => s.roster_id === myRosterId && s.player_id && s.status === 'drafted');

  const addGive    = (pid, name) => { if (!tradeOffer.give.find(x => x.player_id === pid)) setTradeOffer(p => ({ ...p, give: [...p.give, { player_id: pid, player_name: name }] })); };
  const addReceive = (pid, name) => { if (!tradeOffer.receive.find(x => x.player_id === pid)) setTradeOffer(p => ({ ...p, receive: [...p.receive, { player_id: pid, player_name: name }] })); };
  const removeGive    = pid => setTradeOffer(p => ({ ...p, give:    p.give.filter(x => x.player_id !== pid) }));
  const removeReceive = pid => setTradeOffer(p => ({ ...p, receive: p.receive.filter(x => x.player_id !== pid) }));

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={S.modalCard} onClick={e => e.stopPropagation()}>
        <div style={S.modalHeader}>
          ↔ Propose a Trade
          <button style={S.modalClose} onClick={onClose}><Icon emoji="✕" size={14} /></button>
        </div>
        {!tradeResult ? (
          <>
            <div style={S.tradeTarget}>
              <label style={S.tradeLabel}>Trade target:</label>
              <select
                style={S.tradeSelect}
                value={tradeOffer.target_roster_id || ''}
                onChange={e => setTradeOffer(p => ({ ...p, target_roster_id: Number(e.target.value) }))}
              >
                <option value="">— select roster —</option>
                {otherRosters.map(r => (
                  <option key={r.roster_id} value={r.roster_id}>
                    {r.display_name || `Roster ${r.roster_id}`}
                  </option>
                ))}
              </select>
            </div>
            <div style={S.tradeSides}>
              <div style={S.tradeSide}>
                <div style={S.tradeSideLabel}>You give:</div>
                {tradeOffer.give.map(a => (
                  <div key={a.player_id} style={S.tradeAsset}>
                    {a.player_name} <button style={S.tradeRemove} onClick={() => removeGive(a.player_id)}><Icon emoji="✕" size={12} /></button>
                  </div>
                ))}
                <div style={{ ...S.tradeSideLabel, marginTop: 8, fontSize: 11, color: '#AAA' }}>Your drafted:</div>
                <div style={S.tradePlayerList}>
                  {myDrafted.slice(0, 15).map(s => (
                    <button key={s.player_id} style={S.tradeAddBtn} onClick={() => addGive(s.player_id, s.player_name)}>
                      {s.player_name} ({s.position})
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ alignSelf: 'center', fontSize: 24, padding: '0 8px', color: '#888' }}>↔</div>
              <div style={S.tradeSide}>
                <div style={S.tradeSideLabel}>You receive:</div>
                {tradeOffer.receive.map(a => (
                  <div key={a.player_id} style={S.tradeAsset}>
                    {a.player_name} <button style={S.tradeRemove} onClick={() => removeReceive(a.player_id)}><Icon emoji="✕" size={12} /></button>
                  </div>
                ))}
                <div style={{ ...S.tradeSideLabel, marginTop: 8, fontSize: 11, color: '#AAA' }}>Top available:</div>
                <div style={S.tradePlayerList}>
                  {avail.slice(0, 15).map(p => (
                    <button key={p.player_id} style={S.tradeAddBtn} onClick={() => addReceive(p.player_id, p.full_name)}>
                      {p.full_name} ({p.position})
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <button
              style={{ ...S.btnPrimary, marginTop: 16, width: '100%' }}
              onClick={onSubmit}
              disabled={loading || !tradeOffer.give.length || !tradeOffer.receive.length}
            >
              {loading ? 'Evaluating...' : 'Submit Trade Proposal'}
            </button>
          </>
        ) : (
          <TradeResultView result={tradeResult} onClose={onClose} />
        )}
      </div>
    </div>
  );
}

function TradeResultView({ result, onClose }) {
  if (result.error) return <div style={{ color: 'var(--red)', padding: 16 }}>{result.error}</div>;
  const statusColor = { accepted: '#2ECC71', countered: '#F39C12', declined: '#E74C3C' }[result.response] || '#95A5A6';
  return (
    <div style={S.tradeResult}>
      <div style={{ ...S.tradeResponseBadge, background: statusColor }}>{result.response?.toUpperCase()}</div>
      <div style={S.tradeReasoning}>{result.reasoning}</div>
      <div style={S.tradeValues}>
        <span>Your offer: <strong>{result.give_value}</strong></span>
        <span>You receive: <strong>{result.receive_value}</strong></span>
        <span>Delta: <strong style={{ color: result.value_delta >= 0 ? 'var(--green)' : 'var(--red)' }}>
          {result.value_delta >= 0 ? '+' : ''}{result.value_delta}
        </strong></span>
      </div>
      {result.counter && (
        <div style={S.tradeCounter}>
          <div style={S.tradeCounterLabel}>AI Counter:</div>
          <div>{result.counter.note || JSON.stringify(result.counter)}</div>
        </div>
      )}
      <button style={{ ...S.btnSecondary, marginTop: 16 }} onClick={onClose}>Close</button>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const S = {
  // Shell
  shell: {
    display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0,
    background: 'var(--lor-bg, #0D0F14)', color: 'var(--lor-text, #E8E8E8)',
    fontFamily: "'Inter', sans-serif",
  },

  // Top bar
  topBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.07)',
    background: 'rgba(255,255,255,0.03)', flexShrink: 0,
  },
  topBarLeft:   { display: 'flex', alignItems: 'center', gap: 10 },
  topBarCenter: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, flex: 1, padding: '0 16px' },
  topBarRight:  { display: 'flex', alignItems: 'center', gap: 8 },
  topBarTitle:  { fontWeight: 700, fontSize: 15, color: 'var(--lor-gold)' },
  modeBadge: (mode) => ({
    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
    background: mode === 'realistic' ? 'rgba(var(--accent2-rgb),0.2)' : 'rgba(var(--lor-accent-rgb),0.2)',
    color: mode === 'realistic' ? '#A29BFE' : 'var(--lor-gold)',
  }),
  badge: { fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4 },
  savedBadge: {
    border: '1px solid rgba(46,204,113,0.35)', borderRadius: 4,
    background: 'rgba(46,204,113,0.12)', color: 'var(--green)',
    padding: '3px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
  },
  progressBar: { width: 200, height: 5, background: 'rgba(255,255,255,0.08)', borderRadius: 3, overflow: 'hidden' },
  progressFill: { height: '100%', background: 'var(--lor-gold)', borderRadius: 3, transition: 'width 0.4s ease' },
  progressText: { fontSize: 11, color: '#888' },

  // Banners
  myTurnBanner: {
    background: 'rgba(var(--lor-accent-rgb),0.18)', borderBottom: '1px solid rgba(var(--lor-accent-rgb),0.4)',
    color: 'var(--lor-gold)', fontWeight: 800, fontSize: 14, padding: '10px 16px', textAlign: 'center',
    flexShrink: 0, letterSpacing: '0.02em',
  },
  aiTurnBanner: {
    background: 'rgba(162,155,254,0.08)', borderBottom: '1px solid rgba(162,155,254,0.15)',
    color: '#A29BFE', fontSize: 12, padding: '6px 16px', textAlign: 'center',
    flexShrink: 0, animation: 'fade-in 0.3s ease',
  },
  aiToastBanner: {
    background: 'rgba(162,155,254,0.15)', borderBottom: '1px solid rgba(162,155,254,0.25)',
    color: '#C4B9FF', fontSize: 13, padding: '8px 16px', textAlign: 'center',
    flexShrink: 0, fontWeight: 500, animation: 'toast-in 0.25s ease',
  },
  aiToastSub: { marginLeft: 8, fontSize: 11, color: '#888' },
  errBanner: {
    background: 'rgba(231,76,60,0.2)', border: '1px solid rgba(231,76,60,0.4)',
    color: 'var(--red)', padding: '8px 16px', fontSize: 13, flexShrink: 0,
  },

  // Layout
  mainLayout: { display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' },
  boardPanel: {
    width: 380, flexShrink: 0, display: 'flex', flexDirection: 'column',
    borderRight: '1px solid rgba(255,255,255,0.07)', overflowY: 'hidden',
  },
  centerPanel: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflowY: 'hidden' },
  rightPanel: {
    width: 210, flexShrink: 0, borderLeft: '1px solid rgba(255,255,255,0.07)',
    overflowY: 'auto', padding: '0 0 16px',
  },
  panelHeader: {
    display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
    padding: '10px 12px 6px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0,
  },
  panelTitle: { fontWeight: 700, fontSize: 11, color: '#AAA', textTransform: 'uppercase', letterSpacing: '0.06em' },
  panelSub:   { fontSize: 11, color: '#888' },
  viewTabs: { display: 'flex', gap: 0, borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 },
  viewTab: {
    flex: 1, padding: '10px 8px', background: 'none', border: 'none',
    borderBottom: '2px solid transparent', color: '#888', cursor: 'pointer',
    fontSize: 12, fontWeight: 500, transition: 'all 0.15s', minHeight: 44,
  },
  viewTabActive: { color: 'var(--lor-gold)', borderBottomColor: 'var(--lor-gold)' },

  // Sleeper board
  sleeperBoardOuter: { flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  sleeperBoardScroll: {
    flex: 1, overflowX: 'auto', overflowY: 'auto',
    padding: '0 0 8px 0',
    // Minimum table sizing
    minWidth: 0,
  },
  sleeperHeaderRow: {
    display: 'flex', flexShrink: 0,
    position: 'sticky', top: 0, zIndex: 3,
    background: '#0D0F14', borderBottom: '1px solid rgba(255,255,255,0.08)',
  },
  sleeperTeamRow: {
    display: 'flex', flexShrink: 0,
    borderBottom: '1px solid rgba(255,255,255,0.04)',
  },
  sleeperTeamRowMe: {
    background: 'rgba(var(--lor-accent-rgb),0.04)',
  },
  sleeperTeamCol: {
    width: 80, minWidth: 80, flexShrink: 0,
    display: 'flex', flexDirection: 'column', justifyContent: 'center',
    padding: '4px 6px', gap: 1,
    position: 'sticky', left: 0, zIndex: 2,
    background: '#0D0F14', borderRight: '1px solid rgba(255,255,255,0.08)',
  },
  sleeperTeamColMe: {
    background: '#141209',
  },
  sleeperTeamSlot: (isMe) => ({
    fontSize: 9, fontWeight: 700,
    color: isMe ? 'var(--lor-gold)' : '#888',
  }),
  sleeperTeamName: (isMe) => ({
    fontSize: 10, fontWeight: isMe ? 700 : 600,
    color: isMe ? 'var(--lor-gold)' : '#DDD',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    maxWidth: 68,
  }),
  sleeperRndHeader: {
    width: 56, minWidth: 56, flexShrink: 0, flexGrow: 0,
    fontSize: 9, fontWeight: 700, color: '#888', textAlign: 'center',
    padding: '4px 0',
  },
  sleeperCell: {
    width: 56, minWidth: 56, height: 54, flexShrink: 0, flexGrow: 0,
    border: '1px solid rgba(255,255,255,0.05)',
    borderRadius: 3, margin: 1, padding: '3px 2px',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    background: 'rgba(255,255,255,0.015)', fontSize: 8, cursor: 'default',
    overflow: 'hidden', textAlign: 'center',
  },
  sleeperCellMe: {
    borderColor: 'rgba(var(--lor-accent-rgb),0.3)', background: 'rgba(var(--lor-accent-rgb),0.04)',
  },
  sleeperCellNext: {
    borderColor: 'var(--lor-gold)', background: 'rgba(var(--lor-accent-rgb),0.12)',
    animation: 'pulse-cell 1.5s infinite',
  },
  sleeperCellKeeper: {
    borderColor: 'rgba(78,205,196,0.5)', background: 'rgba(78,205,196,0.08)',
  },
  sleeperCellProjK: {
    borderColor: 'rgba(243,156,18,0.4)', background: 'rgba(243,156,18,0.06)',
  },
  sleeperCellAnim: {
    borderColor: 'var(--lor-gold)', background: 'rgba(var(--lor-accent-rgb),0.2)',
    animation: 'pulse-cell 0.6s ease',
  },
  sleeperCellPick: { fontSize: 8, color: '#888', fontWeight: 700, marginBottom: 1 },
  sleeperCellPos: {
    fontSize: 7, fontWeight: 700, padding: '1px 3px', borderRadius: 2,
    marginBottom: 1, lineHeight: 1,
  },
  sleeperCellName: {
    fontSize: 8, color: '#CCC', lineHeight: 1.15,
    wordBreak: 'break-word', maxWidth: 50,
  },
  sleeperCellEmpty: { fontSize: 9, color: '#777', marginTop: 6 },
  keeperBadge:    { fontSize: 7, color: '#4ECDC4', fontWeight: 700, marginTop: 1 },
  projKeeperBadge:{ fontSize: 7, color: '#F39C12', fontWeight: 700, marginTop: 1 },

  // Pick cards
  pickCardsWrap: { flex: 1, overflowY: 'auto', padding: '12px 16px' },
  recsSectionLabel: { fontSize: 12, color: '#888', fontWeight: 600, marginBottom: 12 },
  pickCardList: { display: 'flex', flexDirection: 'column', gap: 10 },
  pickCard: {
    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 10, padding: '12px 14px', cursor: 'default', textAlign: 'left',
    width: '100%', transition: 'all 0.15s',
  },
  pickCardActive: {
    cursor: 'pointer', border: '1px solid rgba(var(--lor-accent-rgb),0.25)',
    background: 'rgba(var(--lor-accent-rgb),0.04)',
  },
  pickCardTop: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 },
  pickCardEmoji: { fontSize: 18 },
  pickCardLabel: { fontSize: 13, fontWeight: 700, color: '#CCC', flex: 1 },
  riskBadge: { fontSize: 10, fontWeight: 600 },
  pickCardPlayer: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 },
  pickCardPos: { fontSize: 14, fontWeight: 800, padding: '3px 10px', borderRadius: 5, flexShrink: 0 },
  pickCardInfo: { flex: 1 },
  pickCardName: { fontSize: 17, fontWeight: 800, color: '#EEE', lineHeight: 1.2 },
  pickCardSub:  { fontSize: 11, color: '#888', marginTop: 2 },
  pickCardReason: { fontSize: 12, color: '#999', lineHeight: 1.5, marginBottom: 8 },
  pickCardDraftBtn: {
    background: 'var(--lor-gold)', color: '#000', fontWeight: 800, fontSize: 14,
    textAlign: 'center', padding: '12px 0', borderRadius: 7, marginTop: 4,
    minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  browseBtn: {
    marginTop: 12, width: '100%', padding: '10px 0',
    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
    color: '#888', fontWeight: 600, fontSize: 13, borderRadius: 7, cursor: 'pointer',
    minHeight: 44,
  },
  upcomingCard: {
    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 6, padding: '8px 10px', marginBottom: 8, fontSize: 11,
  },
  upcomingHeader: { fontWeight: 700, color: '#CCC', display: 'flex', justifyContent: 'space-between', marginBottom: 4 },
  upcomingDist: { fontSize: 10, color: '#888' },
  upcomingSection: { marginTop: 4 },
  upcomingLabel: { fontSize: 10, color: '#AAA', marginBottom: 2 },
  upcomingPlayer: { display: 'inline-block', fontSize: 11, marginRight: 6 },

  // Available panel
  availWrap: { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 },
  filterRow: {
    display: 'flex', gap: 8, padding: '8px 12px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    flexShrink: 0, alignItems: 'center', flexWrap: 'wrap',
  },
  searchInput: {
    flex: 1, minWidth: 100,
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 6, color: '#EEE', padding: '7px 10px', fontSize: 12, outline: 'none',
    minHeight: 36,
  },
  posFilters: { display: 'flex', gap: 3, flexWrap: 'wrap' },
  posFilterBtn: {
    padding: '4px 7px', fontSize: 11, fontWeight: 600, background: 'none',
    border: '1px solid rgba(255,255,255,0.14)', borderRadius: 4, cursor: 'pointer',
    color: '#AAA', transition: 'all 0.15s', minHeight: 32,
  },
  posFilterBtnActive: { background: 'rgba(var(--lor-accent-rgb),0.12)', color: 'var(--lor-gold)', borderColor: 'rgba(var(--lor-accent-rgb),0.4)' },
  availList: { flex: 1, overflowY: 'auto', padding: '4px 0' },
  availRow: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px',
    borderBottom: '1px solid rgba(255,255,255,0.04)', minHeight: 44,
  },
  availRank: { fontSize: 10, color: '#777', width: 24, textAlign: 'right', flexShrink: 0 },
  availPos:  { fontSize: 11, fontWeight: 700, padding: '1px 5px', borderRadius: 3, flexShrink: 0, minWidth: 30, textAlign: 'center' },
  availInfo: { flex: 1, minWidth: 0 },
  availName: { fontSize: 13, fontWeight: 600, color: '#DDD', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  availSub:  { fontSize: 10, color: '#AAA' },
  availPickBtn: {
    padding: '0 12px', background: 'rgba(var(--lor-accent-rgb),0.15)', border: '1px solid rgba(var(--lor-accent-rgb),0.4)',
    color: 'var(--lor-gold)', fontWeight: 700, fontSize: 11, borderRadius: 4, cursor: 'pointer',
    flexShrink: 0, minHeight: 44,
  },
  injuryBadge: { color: 'var(--red)', fontWeight: 700 },

  // AI log
  logWrap: { flex: 1, overflowY: 'auto', padding: '8px 0' },
  logRow: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
  },
  logPickNo: { fontSize: 10, color: '#888', width: 36, flexShrink: 0 },
  logPos:    { fontSize: 10, fontWeight: 700, width: 26, flexShrink: 0 },
  logBody:   { flex: 1, display: 'flex', flexDirection: 'column', gap: 1 },
  logManager:{ fontSize: 11, color: '#A29BFE', fontWeight: 600 },
  logName:   { fontSize: 12, color: '#DDD' },
  logTeam:   { fontSize: 10, color: '#888' },

  // Upcoming sidebar
  upcomingSidebar: { padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6 },
  upcomingSidebarCard: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    background: 'rgba(var(--lor-accent-rgb),0.08)', border: '1px solid rgba(var(--lor-accent-rgb),0.2)',
    borderRadius: 6, padding: '5px 10px',
  },
  upcomingSidebarPick: { fontWeight: 700, fontSize: 13, color: 'var(--lor-gold)' },
  upcomingSidebarDist: { fontSize: 10, color: '#AAA' },

  // My roster
  myRosterWrap: { padding: '8px 10px' },
  rosterPosHeader: { fontSize: 10, fontWeight: 700, marginTop: 8, marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.05em' },
  rosterRow: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 },
  rosterPickDisp: { fontSize: 10, color: '#888', width: 30, flexShrink: 0 },
  rosterName: { fontSize: 11, color: '#CCC', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  keeperMini: { fontSize: 9, color: '#4ECDC4', fontWeight: 700, background: 'rgba(78,205,196,0.15)', padding: '1px 3px', borderRadius: 2 },

  // Analysis
  analysisWrap: { flex: 1, overflowY: 'auto', padding: '16px' },
  gradeHero: { display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 20 },
  gradeCircle: {
    width: 72, height: 72, borderRadius: '50%', border: '3px solid',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 32, fontWeight: 900, flexShrink: 0,
  },
  gradeHeroRight: { flex: 1 },
  gradeHeroTitle: { fontSize: 13, fontWeight: 700, color: '#888', marginBottom: 6 },
  gradeSummaryRow: { display: 'flex', gap: 12, fontSize: 12, marginBottom: 8 },
  tierGradesRow: { display: 'flex', gap: 6 },
  tierGradeCard: {
    flex: 1, textAlign: 'center', background: 'rgba(255,255,255,0.03)',
    border: '1px solid', borderRadius: 6, padding: '6px 2px',
  },
  posTotalsRow: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 },
  posTotalCard: {
    textAlign: 'center', border: '1px solid', borderRadius: 6, padding: '6px 10px', minWidth: 40,
  },
  posTotalNum: { fontSize: 18, fontWeight: 700 },
  posTotalPos: { fontSize: 10, fontWeight: 600 },
  gapWarning: {
    background: 'rgba(231,76,60,0.1)', border: '1px solid rgba(231,76,60,0.3)',
    color: 'var(--red)', borderRadius: 6, padding: '8px 12px', fontSize: 12, marginBottom: 12,
  },
  highlightCard: {
    background: 'rgba(46,204,113,0.06)', border: '1px solid rgba(46,204,113,0.25)',
    borderRadius: 8, padding: '10px 14px', marginBottom: 10,
  },
  highlightLabel: { fontSize: 11, fontWeight: 700, color: 'var(--green)', marginBottom: 4 },
  highlightName:  { fontSize: 15, fontWeight: 700, color: '#EEE' },
  highlightSub:   { fontSize: 11, color: '#888', marginTop: 2 },
  keyRecCard: {
    background: 'rgba(162,155,254,0.07)', border: '1px solid rgba(162,155,254,0.2)',
    borderRadius: 8, padding: '10px 14px', marginBottom: 12,
  },
  keyRecLabel: { fontSize: 11, fontWeight: 700, color: '#A29BFE', marginBottom: 4 },
  keyRecText:  { fontSize: 13, color: '#CCC', lineHeight: 1.5 },
  allPicksLabel: { fontSize: 11, color: '#888', fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 },
  allPicksList:  { display: 'flex', flexDirection: 'column', gap: 4 },
  gradedRow: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0',
    borderBottom: '1px solid rgba(255,255,255,0.04)', fontSize: 12,
  },
  gradedPickDisp: { color: '#888', width: 36, flexShrink: 0 },
  gradedPos: { width: 28, fontWeight: 700, flexShrink: 0 },
  gradedName: { flex: 1, color: '#DDD', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },

  // Full board
  fullBoardWrap: { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' },
  fullBoardToolbar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '8px 14px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0,
  },
  fullBoardTitle: { fontSize: 12, fontWeight: 700, color: '#AAA', textTransform: 'uppercase', letterSpacing: '0.05em' },
  fullBoardToggleBtn: {
    background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.16)',
    color: '#DDD', borderRadius: 5, padding: '5px 12px', fontSize: 11, cursor: 'pointer', minHeight: 36,
  },

  // Lobby
  lobbyWrap: {
    flex: 1, overflowY: 'auto', padding: '24px 16px',
    background: 'var(--lor-bg, #0D0F14)',
  },
  lobbyInner: { maxWidth: 680, margin: '0 auto' },
  lobbyHeader: { textAlign: 'center', marginBottom: 28 },
  lobbyHeaderIcon: { fontSize: 52, marginBottom: 8 },
  lobbyTitle: { fontSize: 26, fontWeight: 900, color: 'var(--lor-gold)', margin: '0 0 6px' },
  lobbySub: { fontSize: 13, color: '#777', margin: 0 },
  lobbySectionLabel: { fontSize: 11, fontWeight: 700, color: '#AAA', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 },
  lobbyTeamsSection: { marginBottom: 24 },
  lobbyTeamGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8,
    '@media (max-width: 480px)': { gridTemplateColumns: 'repeat(2, 1fr)' },
  },
  lobbyTeamCard: {
    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)',
    borderRadius: 8, padding: '10px 8px', textAlign: 'center',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
  },
  lobbyTeamCardMe: {
    border: '2px solid rgba(var(--lor-accent-rgb),0.5)', background: 'rgba(var(--lor-accent-rgb),0.07)',
  },
  lobbySlotBadge: (isMe) => ({
    width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 12, fontWeight: 800,
    background: isMe ? 'rgba(var(--lor-accent-rgb),0.25)' : 'rgba(255,255,255,0.12)',
    color: isMe ? 'var(--lor-gold)' : '#CCC',
    flexShrink: 0,
  }),
  lobbyTeamName: (isMe) => ({
    fontSize: 11, fontWeight: isMe ? 800 : 600,
    color: isMe ? 'var(--lor-gold)' : '#E0E0E0',
    lineHeight: 1.2, wordBreak: 'break-word',
  }),
  lobbyYouSub: { fontSize: 9, color: '#AAA' },
  lobbyConfigRow: { display: 'flex', gap: 20, marginBottom: 20, flexWrap: 'wrap' },
  modeSelector: { display: 'flex', flexDirection: 'column', gap: 8 },
  modeCard: {
    padding: '12px 14px', background: 'rgba(255,255,255,0.05)',
    border: '2px solid rgba(255,255,255,0.14)', borderRadius: 8, cursor: 'pointer',
    textAlign: 'left', color: '#E8E8E8', transition: 'all 0.15s',
  },
  modeCardActive: { borderColor: 'var(--lor-gold)', background: 'rgba(var(--lor-accent-rgb),0.08)' },
  modeCardLabel: { fontSize: 13, fontWeight: 700, marginBottom: 4, color: '#E8E8E8' },
  modeCardDesc:  { fontSize: 11, color: '#AAA', lineHeight: 1.4 },
  speedBtn: {
    padding: '8px 14px', background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.14)', borderRadius: 6,
    color: '#AAA', cursor: 'pointer', fontSize: 12, fontWeight: 500,
    textAlign: 'left', minHeight: 40,
  },
  speedBtnActive: { background: 'rgba(162,155,254,0.15)', borderColor: 'rgba(162,155,254,0.4)', color: '#A29BFE' },
  lobbySlotInfo: {
    background: 'rgba(var(--lor-accent-rgb),0.08)', border: '1px solid rgba(var(--lor-accent-rgb),0.25)',
    borderRadius: 8, padding: '10px 16px', fontSize: 13, color: 'var(--lor-gold)', marginBottom: 16,
  },
  lobbyFeatures: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 },
  lobbyFeatureItem: {
    fontSize: 11, color: '#AAA',
    background: 'rgba(255,255,255,0.06)', borderRadius: 4, padding: '4px 8px',
  },
  lobbyHistorySection: { marginTop: 12 },
  lobbyHistoryButton: {
    width: '100%', minHeight: 46, padding: '11px 14px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.14)',
    borderRadius: 8, color: '#DDD', cursor: 'pointer', fontSize: 13, fontWeight: 700,
    textAlign: 'left',
  },
  lobbyHistoryPanel: {
    marginTop: 8, maxHeight: 'min(560px, 65vh)', overflowY: 'auto',
    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8,
  },

  // Mobile
  mobileShell: { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, paddingBottom: 60 },
  mobileContent: { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' },
  mobileNav: {
    position: 'fixed', bottom: 0, left: 0, right: 0, height: 60,
    display: 'flex', background: '#0D0F14',
    borderTop: '1px solid rgba(255,255,255,0.08)', zIndex: 100,
  },
  mobileNavBtn: {
    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', background: 'none', border: 'none',
    color: '#888', cursor: 'pointer', minHeight: 44,
  },
  mobileNavBtnActive: { color: 'var(--lor-gold)' },

  // Trade modal
  modalOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  modalCard: {
    background: '#17191F', border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 12, padding: 24, width: 660, maxWidth: '95vw', maxHeight: '85vh', overflowY: 'auto',
  },
  modalHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    fontSize: 16, fontWeight: 700, color: 'var(--lor-gold)', marginBottom: 16,
  },
  modalClose: { background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: 18, minHeight: 44, minWidth: 44 },
  tradeTarget:  { marginBottom: 16 },
  tradeLabel:   { fontSize: 12, color: '#AAA', display: 'block', marginBottom: 6 },
  tradeSelect:  { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, color: '#EEE', padding: '6px 10px', fontSize: 13, width: '100%' },
  tradeSides:   { display: 'flex', gap: 12, alignItems: 'flex-start' },
  tradeSide:    { flex: 1, minWidth: 0 },
  tradeSideLabel: { fontSize: 12, fontWeight: 700, color: '#CCC', marginBottom: 6 },
  tradeAsset: {
    display: 'flex', justifyContent: 'space-between',
    background: 'rgba(255,255,255,0.05)', borderRadius: 4, padding: '4px 8px',
    marginBottom: 4, fontSize: 12, color: '#EEE',
  },
  tradeRemove: { background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 12 },
  tradePlayerList: { display: 'flex', flexDirection: 'column', gap: 3, maxHeight: 180, overflowY: 'auto' },
  tradeAddBtn: {
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 4, color: '#CCC', padding: '4px 8px', fontSize: 11, cursor: 'pointer', textAlign: 'left',
  },
  tradeResult: { padding: '8px 0' },
  tradeResponseBadge: {
    display: 'inline-block', padding: '4px 16px', borderRadius: 20,
    fontSize: 13, fontWeight: 800, color: '#000', marginBottom: 12,
  },
  tradeReasoning: { fontSize: 13, color: '#CCC', marginBottom: 12, lineHeight: 1.5 },
  tradeValues:    { display: 'flex', gap: 16, fontSize: 12, color: '#888', marginBottom: 12 },
  tradeCounter: {
    background: 'rgba(243,156,18,0.1)', border: '1px solid rgba(243,156,18,0.3)',
    borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#F39C12',
  },
  tradeCounterLabel: { fontWeight: 700, marginBottom: 4 },

  // Shared
  emptyState: { color: '#888', fontSize: 13, textAlign: 'center', padding: 24 },
  btnPrimary: {
    background: 'var(--lor-gold)', color: '#000', fontWeight: 800, border: 'none',
    borderRadius: 8, padding: '10px 24px', cursor: 'pointer', fontSize: 14,
    transition: 'opacity 0.15s', minHeight: 44,
  },
  btnSecondary: {
    background: 'rgba(255,255,255,0.08)', color: '#CCC', fontWeight: 600,
    border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, padding: '6px 14px',
    cursor: 'pointer', fontSize: 12, minHeight: 36,
  },
  btnDanger: {
    background: 'rgba(231,76,60,0.15)', color: 'var(--red)', fontWeight: 600,
    border: '1px solid rgba(231,76,60,0.3)', borderRadius: 6, padding: '6px 14px',
    cursor: 'pointer', fontSize: 12, minHeight: 36,
  },
};
